/**
 * Place modal component
 * Full place details modal
 */

import { Modal, Typography, Rate, Tag, Space, Button, Image, Descriptions, Tabs, List, Skeleton, Divider, Row, Col } from 'antd';
import {
  EnvironmentOutlined,
  PhoneOutlined,
  GlobalOutlined,
  ClockCircleOutlined,
  ExportOutlined,
  CopyOutlined,
  StarOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { Place, PlaceReview } from '@/types/place';
import { formatNumber, formatDate } from '@/utils/formatters';

const { Text, Paragraph, Link, Title } = Typography;

interface PlaceModalProps {
  place: Place | null;
  open: boolean;
  onClose: () => void;
  loading?: boolean;
}

export function PlaceModal({ place, open, onClose, loading }: PlaceModalProps) {
  if (!place && !loading) return null;

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const tabItems = [
    {
      key: 'details',
      label: 'Details',
      children: place && (
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="Name">{place.name}</Descriptions.Item>
          <Descriptions.Item label="Category">
            {place.category || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Address">
            {place.address ? (
              <Space>
                <Text copyable>{place.address}</Text>
              </Space>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Plus Code">
            {place.plusCode ? <Text copyable>{place.plusCode}</Text> : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Phone">
            {place.phone ? <Text copyable>{place.phone}</Text> : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Website">
            {place.website ? (
              <Link href={place.website} target="_blank">
                {place.website}
              </Link>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Price Level">
            {place.priceLevel || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Description">
            {place.description || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Service Options">
            {place.serviceOptions && place.serviceOptions.length > 0 ? (
              <Space wrap>
                {place.serviceOptions.map((opt, i) => (
                  <Tag key={i}>{opt}</Tag>
                ))}
              </Space>
            ) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Amenities">
            {place.amenities && place.amenities.length > 0 ? (
              <Space wrap>
                {place.amenities.map((a, i) => (
                  <Tag key={i} color="blue">{a}</Tag>
                ))}
              </Space>
            ) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Owner Verified">
            {place.ownerVerified ? (
              <Tag color="green">Verified</Tag>
            ) : (
              <Tag>Not Verified</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Coordinates">
            {place.latitude && place.longitude ? (
              <Text copyable>
                {place.latitude.toFixed(6)}, {place.longitude.toFixed(6)}
              </Text>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Place ID">
            <Text copyable code style={{ fontSize: 11 }}>
              {place.placeId}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Scraped At">
            {place.scrapedAt ? formatDate(place.scrapedAt) : '-'}
          </Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'hours',
      label: 'Opening Hours',
      children: place?.openingHours ? (
        <List
          size="small"
          dataSource={Object.entries(place.openingHours)}
          renderItem={([day, hours]) => (
            <List.Item>
              <Text strong style={{ width: 100 }}>
                {day}
              </Text>
              <Text>{hours}</Text>
            </List.Item>
          )}
        />
      ) : (
        <Text type="secondary">No opening hours available</Text>
      ),
    },
    {
      key: 'reviews',
      label: `Reviews (${place?.reviewCount || 0})`,
      children: place?.reviews && place.reviews.length > 0 ? (
        <List
          size="small"
          dataSource={place.reviews.slice(0, 10)}
          renderItem={(review: PlaceReview) => (
            <List.Item>
              <List.Item.Meta
                avatar={<UserOutlined />}
                title={
                  <Space>
                    <Text strong>{review.author}</Text>
                    <Rate
                      disabled
                      value={review.rating}
                      style={{ fontSize: 12 }}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {formatDate(review.date)}
                    </Text>
                  </Space>
                }
                description={
                  <Paragraph
                    ellipsis={{ rows: 3, expandable: true, symbol: 'more' }}
                    style={{ marginBottom: 0 }}
                  >
                    {review.text}
                  </Paragraph>
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <Text type="secondary">No reviews available</Text>
      ),
    },
  ];

  return (
    <Modal
      title={null}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        place?.url && (
          <Button
            key="maps"
            type="primary"
            icon={<ExportOutlined />}
            href={place.googleMapsUrl}
            target="_blank"
          >
            Open in Maps
          </Button>
        ),
      ]}
      width={700}
      styles={{ body: { padding: 0 } }}
    >
      {loading ? (
        <div style={{ padding: 24 }}>
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      ) : place ? (
        <>
          {/* Header with image */}
          <div
            style={{
              position: 'relative',
              height: place.images?.length ? 200 : 0,
              overflow: 'hidden',
            }}
          >
            {place.images && place.images.length > 0 && (
              <Image.PreviewGroup>
                <div style={{ display: 'flex', gap: 4, overflow: 'hidden' }}>
                  {place.images.slice(0, 3).map((photo, index) => (
                    <Image
                      key={index}
                      src={photo}
                      height={200}
                      width={place.images!.length === 1 ? '100%' : undefined}
                      style={{ objectFit: 'cover', flex: 1 }}
                    />
                  ))}
                </div>
              </Image.PreviewGroup>
            )}
          </div>

          {/* Content */}
          <div style={{ padding: 24 }}>
            {/* Title and rating */}
            <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
              <Col flex="auto">
                <Title level={4} style={{ marginBottom: 4 }}>
                  {place.name}
                </Title>
                {place.category && <Tag>{place.category}</Tag>}
              </Col>
              {place.rating && (
                <Col>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 28, fontWeight: 600 }}>
                      {place.rating.toFixed(1)}
                    </div>
                    <Rate
                      disabled
                      value={place.rating}
                      allowHalf
                      style={{ fontSize: 14 }}
                    />
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatNumber(place.reviewCount || 0)} reviews
                      </Text>
                    </div>
                  </div>
                </Col>
              )}
            </Row>

            {/* Quick info */}
            <Space direction="vertical" size={8} style={{ marginBottom: 24 }}>
              {place.address && (
                <Space>
                  <EnvironmentOutlined style={{ color: 'var(--color-text-tertiary)' }} />
                  <Text>{place.address}</Text>
                </Space>
              )}
              {place.phone && (
                <Space>
                  <PhoneOutlined style={{ color: 'var(--color-text-tertiary)' }} />
                  <Text copyable>{place.phone}</Text>
                </Space>
              )}
              {place.website && (
                <Space>
                  <GlobalOutlined style={{ color: 'var(--color-text-tertiary)' }} />
                  <Link href={place.website} target="_blank">
                    {new URL(place.website).hostname}
                  </Link>
                </Space>
              )}
            </Space>

            <Divider style={{ margin: '16px 0' }} />

            {/* Tabs */}
            <Tabs items={tabItems} size="small" />
          </div>
        </>
      ) : null}
    </Modal>
  );
}

export default PlaceModal;
